import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  Card,
  Button,
  Tag,
  Input,
  Row,
  Col,
  App,
  Image,
  Typography,
  Popconfirm,
  Space,
  Switch,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  HomeOutlined,
  EyeOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { PropertyArea } from '../../types/property';
import { UserPropertyArea } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { debounce } from 'lodash';

const { Title, Text } = Typography;

const PropertyAreaListPage: React.FC = () => {
  const [propertyAreas, setPropertyAreas] = useState<PropertyArea[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
  });
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { user, hasPermission } = useAuth();

  const userPropertyAreaIds = user?.user_property_areas?.map((upa: UserPropertyArea) => upa.property_area_id) || [];

  const { current: currentPage, pageSize } = pagination;

  const fetchPropertyAreas = useCallback(async (currentFilters: typeof filters, currentPage: number, currentPageSize: number) => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: currentPageSize,
        search: currentFilters.search,
      };
      const response = await api.get('property-areas', { params });
      if (response.data && response.data.status === 'success') {
        const areas = response.data.data || [];
        setPropertyAreas(areas);
        setPagination(prev => ({
          ...prev,
          total: response.data.meta?.total || areas.length,
          current: currentPage,
          pageSize: currentPageSize,
        }));
      } else {
        message.error('Format respons API tidak sesuai.');
      }
    } catch (error: any) {
      console.error('Failed to fetch property areas:', error);
      message.error('Gagal memuat data property area');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchPropertyAreas(filters, currentPage, pageSize);
  }, [filters, currentPage, pageSize, fetchPropertyAreas]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFilters(prev => ({ ...prev, search: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
      }, 500),
    []
  );

  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleToggleActive = async (record: PropertyArea) => {
    try {
      const action = record.is_active ? 'deactivated' : 'reactivated';
      const endpoint = record.is_active
        ? `property-areas/${record.id}`
        : `property-areas/${record.id}/reactivate`;

      if (record.is_active) {
        await api.delete(endpoint);
      } else {
        await api.put(endpoint);
      }

      message.success(`Property area berhasil di-${action}`);
      fetchPropertyAreas(filters, pagination.current, pagination.pageSize);
    } catch (error) {
      const action = record.is_active ? 'nonaktifkan' : 'aktifkan';
      message.error(`Gagal ${action} property area`);
      console.error(error);
    }
  };

  const columns: any[] = [
    {
      title: 'Property Area Info',
      key: 'property_area_info',
      render: (_: any, record: PropertyArea) => {
        const firstImage = record.photos && record.photos.length > 0 ? record.photos[0].image_url : null;
        return (
          <Space align="start">
            {firstImage ? (
              <Image
                width={80}
                height={60}
                src={firstImage}
                alt={record.name}
                style={{ objectFit: 'cover', borderRadius: 4 }}
              />
            ) : (
              <div style={{
                width: 80,
                height: 60,
                backgroundColor: '#f0f0f0',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <HomeOutlined style={{ fontSize: 24, color: '#ccc' }} />
              </div>
            )}
            <div>
              <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
                {record.name}
              </Title>
              <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                {record.address || 'Alamat tidak tersedia'}
              </Text>
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                Kontak: {record.phone_number || '-'}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 150,
      render: (isActive: boolean, record: PropertyArea) => {
        const canToggle = hasPermission('propertyArea.delete') && userPropertyAreaIds.includes(record.id);
        return (
          <Space>
            <Tag color={isActive ? 'green' : 'red'}>
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
            <Tooltip title={!canToggle ? "Anda tidak memiliki izin untuk mengubah status area ini" : ""}>
              <span style={!canToggle ? { cursor: 'not-allowed', opacity: 0.5 } : {}}>
                <Popconfirm
                  title={`Yakin ingin ${record.is_active ? 'nonaktifkan' : 'aktifkan'} area ini?`}
                  onConfirm={() => handleToggleActive(record)}
                  okText="Ya"
                  cancelText="Tidak"
                  disabled={!canToggle}
                >
                  <Switch
                    checked={isActive}
                    disabled={!canToggle}
                  />
                </Popconfirm>
              </span>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      width: 120,
      align: 'center',
      render: (_: any, record: PropertyArea) => {
        const canEdit = hasPermission('propertyArea.update') && userPropertyAreaIds.includes(record.id);
        const isEditDisabled = !record.is_active || !canEdit;

        let editTooltip = "";
        if (!record.is_active) {
          editTooltip = "Area properti harus aktif untuk bisa diedit.";
        } else if (!canEdit) {
          editTooltip = "Anda tidak memiliki izin untuk mengedit area ini.";
        }

        return (
          <Space size="middle">
            <Tooltip title="Lihat Detail">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/property/areas/${record.id}`)}
              />
            </Tooltip>
            <Tooltip title={editTooltip}>
              <span>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/property/areas/${record.id}/edit`)}
                  disabled={isEditDisabled}
                />
              </span>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Manajemen Property Area
          </Title>
        </Col>
        <Col>
          {hasPermission('propertyArea.create') && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/property/areas/create')}
            >
              Tambah Property Area
            </Button>
          )}
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Cari nama atau alamat..."
              prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              onChange={(e) => debouncedSearch(e.target.value)}
              allowClear
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={propertyAreas}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} area`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default PropertyAreaListPage;
